/**
 * AVA-475 — WYSIWYG evidence capture from the Forge viewer.
 *
 * LITERAL WYSIWYG. Whatever is on screen at the moment of the click is the
 * evidence: current camera, slice, zoom, scan visibility, markers, the axis
 * triad and its labels, and the crop box if the surveyor has it showing.
 * Nothing is re-framed and nothing is hidden. An image that quietly differs
 * from what the surveyor was looking at is not evidence of anything.
 *
 * THE CAPTURE PATH IS THE WHOLE PROBLEM — read this before editing:
 *
 * `viewer.getScreenShot` does an OFFSCREEN RE-RENDER. It works, and it was the
 * original implementation, but it renders the model without the overlay scenes
 * — so the axis triad, its labels and other overlay content are missing from
 * the result. That was reported from live testing, which also tells us the
 * offscreen path genuinely produces an image: it is correct-but-incomplete,
 * not broken.
 *
 * So the primary path is now a LIVE CANVAS GRAB: force a repaint and copy
 * `viewer.canvas` inside the frame-rendered handler, in the same task as the
 * render. That captures exactly the composited frame, overlays included.
 *
 * IT HAS A KNOWN RISK AND THAT RISK IS DETECTED, NOT ASSUMED. A WebGL drawing
 * buffer is cleared after compositing unless the context was created with
 * `preserveDrawingBuffer: true`, and the Forge viewer here is NOT created that
 * way (`src/scripts/js/forgeviewer.js:109`). Whether the copy lands before the
 * clear depends on where Forge dispatches the frame event, which cannot be
 * settled by reading source. So the grabbed frame is PROBED for blankness and,
 * if blank, we fall back to the known-working offscreen re-render and say so in
 * the log. Shipping a black rectangle as conformance evidence would be a worse
 * failure than the missing overlays this replaced.
 *
 * The permanent fix, should the fallback turn out to fire, is the
 * `preserveDrawingBuffer` monkey-patch this repo already uses for Potree
 * (`PotreeViewer.vue:170-180`) applied before the Forge viewer's canvas gets
 * its context. That is a one-line change in `forgeviewer.js` and deliberately
 * NOT done here: it has to run before viewer creation, and this module runs at
 * capture time.
 *
 * THE CAPTURE IS A COMPOSITE, because the picture is. Live testing confirmed
 * the canvas grab carries the point cloud and the axis triad with its labels —
 * but the verification point markers are not scene content at all. They are DOM
 * elements (`.verification-marker`, a sibling layer over the viewer) positioned
 * each frame from `viewer.worldToClient`. No canvas grab can ever see them. So
 * the marker layer is rasterised separately with html2canvas and drawn on top.
 *
 * ONLY THE MARKER LAYER IS RASTERISED, never the viewer container. The in-repo
 * precedent (`TagScreenshot.vue`) shoots a whole container and then hides the
 * app's toolbars by mutating z-index and visibility, restoring them afterwards
 * — and not in a `finally`, so a failure there leaves the UI broken. Targeting
 * the overlay layer alone means the status bar and the Forge toolbar cannot
 * appear in the shot BY CONSTRUCTION rather than by remembering to hide them,
 * nothing in the live DOM is modified, and html2canvas never has to deal with
 * the WebGL canvas.
 *
 * `forgeService.takeScreenshot` IS NOT USABLE for any of this — it routes
 * through `convertBlobUrlToBase64`, which downscales to THUMBNAIL_CONFIG
 * (640x480, JPEG 0.7) for a backend column capped at 500 000 base64 chars. At
 * that size a ±3 mm deviation is a couple of pixels and the image proves
 * nothing.
 */
import html2canvas from 'html2canvas';
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

/**
 * Long edge of the stored image, in pixels.
 *
 * Sized against the job, not a display: an evidence image gets zoomed into on a
 * laptop to check the picked point sits on the steelwork. 1600 survives that and
 * still keeps a handful of them inside a sane PDF.
 */
const CAPTURE_LONG_EDGE = 1600;

const EVIDENCE_FORMAT = 'image/jpeg';

/**
 * Deliberately higher than THUMBNAIL_CONFIG's 0.7. These are read as evidence,
 * and JPEG ringing around thin steel edges at 0.7 is exactly the detail that
 * matters here.
 */
const EVIDENCE_QUALITY = 0.85;

/**
 * Frame and screenshot waits. Neither callback has an error channel — a failure
 * inside the renderer simply never calls back — so both are bounded, or the
 * capture button sits in its busy state for the rest of the session.
 */
const FRAME_TIMEOUT_MS = 4000;
const SCREENSHOT_TIMEOUT_MS = 8000;

/** Fallback frame size when the viewer container has not been measured yet. */
const FALLBACK_ASPECT = 16 / 9;

/** Points probed when deciding whether a grabbed frame is blank. */
const BLANK_PROBE_GRID = 5;

/**
 * Which path produced the image. Logged; the first thing to look at when a
 * capture comes out wrong. `+dom` means the marker layer was composited on top.
 */
export const CAPTURE_PATH = Object.freeze({
  LIVE: 'live-canvas',
  OFFSCREEN: 'offscreen-rerender',
  DOM_SUFFIX: '+dom',
});

/**
 * How the marker layer is found: the viewer's own wrapper, then the overlay
 * inside it.
 *
 * Scoped through the viewer's container rather than a bare
 * `document.querySelector` so a stale layer left by another route can never be
 * captured instead of the live one.
 */
const VIEWER_ROOT_SELECTOR = '.verification-viewer';
const OVERLAY_LAYER_SELECTOR = '.verification-markers';

/** Capture dimensions: CAPTURE_LONG_EDGE wide, viewer aspect preserved. */
function captureSize(viewer) {
  const el = viewer.container || viewer.canvas || null;
  const w = el?.clientWidth || el?.width || 0;
  const h = el?.clientHeight || el?.height || 0;
  const aspect = w > 0 && h > 0 ? w / h : FALLBACK_ASPECT;
  return {
    width: CAPTURE_LONG_EDGE,
    height: Math.max(1, Math.round(CAPTURE_LONG_EDGE / aspect)),
  };
}

function createTarget(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/**
 * True when every probed pixel is identical AND that colour is transparent or
 * black — the signature of a cleared drawing buffer.
 *
 * A uniformly black frame from a camera pointed at nothing matches too, and
 * that is fine: the consequence is a fallback capture of the same empty view.
 * False positives here cost nothing. A false NEGATIVE ships a black rectangle
 * as evidence, so the test is deliberately biased towards calling it blank.
 */
function isBlankFrame(canvas) {
  const ctx = canvas.getContext('2d');
  const step = 1 / (BLANK_PROBE_GRID + 1);
  let first = null;

  for (let row = 1; row <= BLANK_PROBE_GRID; row += 1) {
    for (let col = 1; col <= BLANK_PROBE_GRID; col += 1) {
      const x = Math.floor(canvas.width * step * col);
      const y = Math.floor(canvas.height * step * row);
      const [r, g, b, a] = ctx.getImageData(x, y, 1, 1).data;
      if (first === null) first = [r, g, b, a];
      else if (r !== first[0] || g !== first[1] || b !== first[2] || a !== first[3]) return false;
    }
  }

  if (!first) return true;
  const [r, g, b, a] = first;
  return a === 0 || (r === 0 && g === 0 && b === 0);
}

/**
 * PRIMARY PATH. Copy the live composited frame, overlays and all.
 *
 * The `drawImage` MUST happen synchronously inside the frame handler — that is
 * the entire point. Once those pixels are in a 2D canvas they are safe to touch
 * whenever; before that they exist only until the compositor clears the WebGL
 * buffer. Anything moved to a later tick here silently reintroduces the blank
 * frame this is written to avoid.
 */
function grabLiveCanvas(viewer, width, height) {
  return new Promise((resolve, reject) => {
    const source = viewer.canvas;
    const frameEvent = window.Autodesk?.Viewing?.FINAL_FRAME_RENDERED_CHANGED_EVENT;
    if (!source || !frameEvent) {
      reject(new Error('viewer canvas or frame event unavailable'));
      return;
    }

    let settled = false;
    let timer = null;

    function onFrame(event) {
      // Progressive rendering fires this repeatedly; only the final frame is
      // the complete picture.
      if (event?.value && event.value.finalFrame === false) return;
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        viewer.removeEventListener(frameEvent, onFrame);
      } catch (err) {
        // The viewer can be torn down mid-capture; nothing to detach then.
      }
      try {
        const target = createTarget(width, height);
        target.getContext('2d').drawImage(source, 0, 0, width, height);
        resolve(target);
      } catch (err) {
        reject(err);
      }
    }

    timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      try {
        viewer.removeEventListener(frameEvent, onFrame);
      } catch (err) {
        // As above.
      }
      reject(new Error('timed out waiting for a rendered frame'));
    }, FRAME_TIMEOUT_MS);

    viewer.addEventListener(frameEvent, onFrame);
    // Forces the render whose completion the handler above is waiting for.
    viewer.impl?.invalidate?.(true, true, true);
  });
}

/**
 * FALLBACK PATH. The offscreen re-render — known to work, known to omit overlay
 * content. Kept because a correct-but-incomplete image beats no image.
 */
function grabOffscreen(viewer, width, height) {
  return new Promise((resolve, reject) => {
    if (typeof viewer.getScreenShot !== 'function') {
      reject(new Error('viewer cannot produce a screenshot'));
      return;
    }
    const timer = setTimeout(
      () => reject(new Error('viewer screenshot timed out')),
      SCREENSHOT_TIMEOUT_MS,
    );
    try {
      viewer.getScreenShot(width, height, (url) => {
        clearTimeout(timer);
        if (!url) {
          reject(new Error('viewer screenshot returned no image'));
          return;
        }
        // A blob URL on some builds, a data URL on others; both load into an
        // Image, so nothing downstream needs to care which.
        const image = new Image();
        image.crossOrigin = 'Anonymous';
        image.onload = () => {
          try {
            const target = createTarget(width, height);
            target.getContext('2d').drawImage(image, 0, 0, width, height);
            resolve(target);
          } catch (err) {
            reject(err);
          }
        };
        image.onerror = () => reject(new Error('captured frame could not be decoded'));
        image.src = url;
      });
    } catch (err) {
      clearTimeout(timer);
      reject(err);
    }
  });
}

/**
 * Where the rasterised overlay lands on the output bitmap.
 *
 * PURE, and separated out because it is the one part of the composite that can
 * be proven without a browser. The overlay layer and the canvas are normally
 * the same box, but nothing here assumes it: the offset is measured, so an
 * overlay inset from the canvas still lands over the right pixels instead of
 * being stretched across the whole frame.
 *
 * @param {{left,top,width,height}} canvasRect Viewer canvas, CSS pixels.
 * @param {{left,top,width,height}} overlayRect Marker layer, CSS pixels.
 * @param {number} outWidth Output bitmap width.
 * @param {number} outHeight Output bitmap height.
 * @returns {{x,y,width,height,scale}} Destination rect, output pixels.
 */
export function overlayPlacement(canvasRect, overlayRect, outWidth, outHeight) {
  const scaleX = canvasRect.width > 0 ? outWidth / canvasRect.width : 1;
  const scaleY = canvasRect.height > 0 ? outHeight / canvasRect.height : 1;
  return {
    x: (overlayRect.left - canvasRect.left) * scaleX,
    y: (overlayRect.top - canvasRect.top) * scaleY,
    width: overlayRect.width * scaleX,
    height: overlayRect.height * scaleY,
    // One number for html2canvas. The two axes only diverge if the viewport
    // aspect drifted between measuring and capturing; the larger keeps the
    // rasterised layer from being upscaled after the fact.
    scale: Math.max(scaleX, scaleY),
  };
}

/** The live marker layer for this viewer, or null when there is none. */
function findOverlayLayer(viewer) {
  const root = viewer.container?.closest?.(VIEWER_ROOT_SELECTOR);
  return root?.querySelector(OVERLAY_LAYER_SELECTOR) || null;
}

/**
 * Rasterise the marker layer and draw it over the captured frame.
 *
 * Nothing in the live DOM is touched: html2canvas clones into its own detached
 * iframe, so there is no substitution to undo and no window in which the
 * surveyor could see the viewer flicker.
 *
 * @returns {Promise<boolean>} true when markers were actually drawn.
 */
async function compositeOverlay(target, viewer, width, height) {
  const overlay = findOverlayLayer(viewer);
  // No layer, or markers switched off / all off-screen: nothing to add, and
  // saying so keeps `capturePath` honest about what the image contains.
  if (!overlay || !overlay.childElementCount) return false;

  const overlayRect = overlay.getBoundingClientRect();
  if (!overlayRect.width || !overlayRect.height) return false;
  // Falling back to the overlay's own rect assumes the two are coincident,
  // which is the normal case; it is only reached if the canvas has gone.
  const canvasRect = viewer.canvas?.getBoundingClientRect?.() || overlayRect;

  const place = overlayPlacement(canvasRect, overlayRect, width, height);
  const rasterized = await html2canvas(overlay, {
    // Transparent, so the frame underneath shows through everywhere the
    // markers are not.
    backgroundColor: null,
    scale: place.scale,
    logging: false,
    useCORS: true,
    width: overlayRect.width,
    height: overlayRect.height,
  });

  target.getContext('2d').drawImage(rasterized, place.x, place.y, place.width, place.height);
  return true;
}

/**
 * Capture the viewer exactly as it currently stands.
 *
 * @param {object} viewer Forge viewer instance.
 * @returns {Promise<{dataUrl,width,height,capturedAt,capturePath}>}
 * @throws when neither path can produce a frame — callers must not record
 *   partial evidence.
 */
export async function captureViewerEvidence(viewer) {
  if (!viewer) throw new Error('no viewer to capture');

  const { width, height } = captureSize(viewer);
  let canvas = null;
  let capturePath = CAPTURE_PATH.LIVE;

  try {
    canvas = await grabLiveCanvas(viewer, width, height);
    if (isBlankFrame(canvas)) {
      debugWarn(
        '[evidenceCapture] live frame came back blank — the viewer context has no '
        + 'preserveDrawingBuffer. Falling back to the offscreen re-render; overlays '
        + '(axis triad, labels) will be missing from this image.',
      );
      canvas = null;
    }
  } catch (err) {
    debugWarn('[evidenceCapture] live canvas grab failed; falling back', err);
    canvas = null;
  }

  if (!canvas) {
    capturePath = CAPTURE_PATH.OFFSCREEN;
    canvas = await grabOffscreen(viewer, width, height);
  }

  // Markers are DOM, so they are composited regardless of which path produced
  // the frame underneath. A failure here costs the markers, never the capture:
  // a frame without its marker chips is still usable evidence.
  try {
    if (await compositeOverlay(canvas, viewer, width, height)) {
      capturePath += CAPTURE_PATH.DOM_SUFFIX;
    }
  } catch (err) {
    debugWarn('[evidenceCapture] marker overlay could not be composited', err);
  }

  const dataUrl = canvas.toDataURL(EVIDENCE_FORMAT, EVIDENCE_QUALITY);
  debugLog('[evidenceCapture] captured', {
    capturePath,
    width,
    height,
    // Bytes, roughly — base64 is 4 chars per 3. Worth watching: every image is
    // held in memory and embedded whole into the PDF.
    approxKb: Math.round((dataUrl.length * 0.75) / 1024),
  });

  return {
    dataUrl,
    width,
    height,
    capturedAt: new Date().toISOString(),
    capturePath,
  };
}

/**
 * Capture time as `HH:MM`, for the thumbnail strip and the report caption.
 *
 * Lives here rather than in `reportFormat` because it belongs to the evidence
 * record, and it is shared so the card and the report cannot label the same
 * image with two different times. Local time deliberately: the surveyor
 * correlates these against their own site log, not against UTC.
 */
export function formatEvidenceTime(capturedAt) {
  const when = new Date(capturedAt);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Beyond this many images on one point the report gets heavy and the point's
 * images start flowing over page breaks. A SOFT cap — surfaced as advice in the
 * card, never enforced. A surveyor who genuinely needs eight photographs of an
 * awkward support should not be stopped by a layout preference.
 */
export const EVIDENCE_SOFT_CAP = 6;

export default captureViewerEvidence;
